/**
 * Getting started — FR-HELP-1.
 *
 * The club learned the spreadsheet by watching somebody use it. That does not
 * scale past the people who were standing there, and it is why a new member's
 * first session is spent asking rather than logging.
 *
 * ── what this deliberately is not ───────────────────────────────────────────
 *
 * Not a feature tour. It does not enumerate screens, and anything a member does
 * not need on their first Saturday is not in it — no admin, no duplicate
 * resolution, no offline queue, no statistics beyond "where they appear".
 * Someone who has never opened the app should be able to log a game from this
 * page alone, and every sentence that does not serve that makes the page worse.
 *
 * Static by construction. No API call, no state, nothing to load — so it works
 * on a court with no signal, which is exactly where somebody will first need
 * it. It is also reachable from the sign-in screen, because "how does this
 * work" is a question people have *before* they have an account.
 *
 * On scoring: FR-HELP-1 says to link to the rules page rather than restate the
 * rules, so the two cannot drift. That page is FR-STAT-13, BUILD_PLAN 2.4, and
 * is not built. Rather than link to nothing, the one line the entry path
 * actually depends on is stated here and marked as the thing to replace with a
 * link when 2.4 lands.
 */

export function Guide({ onClose }: { onClose: () => void }): React.JSX.Element {
  return (
    <main className="session guide">
      <header className="session-head">
        <strong>Getting started</strong>
        <button className="link" onClick={onClose}>
          Done
        </button>
      </header>

      <p className="lede">
        Six things, in the order you will do them on a Saturday. It takes about
        two minutes to read and you only need it once.
      </p>

      <Step n={1} title="Open the session">
        <p>
          Somebody creates the session for the day — usually whoever gets there
          first. After that it opens by itself when you start the app, so most
          mornings there is nothing to do here.
        </p>
      </Step>

      <Step n={2} title="Say who is here">
        <p>
          Tap <b>Who&apos;s here?</b> and tap a name to add them. Tap again to
          take somebody off.
        </p>
        <p>
          Somebody visiting who is not on the list? Type their name in the box at
          the bottom and tap <b>Add</b>. They appear straight away, marked as a
          guest, and their games count like anyone else&apos;s.
        </p>
      </Step>

      <Step n={3} title="Log a game — two taps">
        <p>This is the whole point of the app, so it is the shortest thing in it.</p>
        <ol className="taps">
          <li>
            Tap the side that <b>won</b>.
          </li>
          <li>
            Tap what the <b>other side</b> got.
          </li>
        </ol>
        <p>
          That is it. No typing, and it works with no signal — what you log is
          kept on your phone and sent when you have a connection again.
        </p>
        <p className="muted small">
          The app only ever offers scores that are actually possible, so you
          cannot enter 15&ndash;14 by mistake. If a game went to a different
          target, change it on the <b>15 / 21 / 11</b> chips before you tap the
          winner.
        </p>
      </Step>

      <Step n={4} title="Fix one that was wrong">
        <p>
          Go to <b>Games</b>, find it, tap <b>Edit</b>. You can change the score,
          the winner, or who was playing. The most recent game also has{" "}
          <b>Undo</b>.
        </p>
        <p className="muted small">
          Every change is recorded and shown at the end of the session. Nothing
          is hidden and nothing is lost — that is the point of editing rather
          than deleting and re-entering.
        </p>
      </Step>

      <Step n={5} title="See how it is going">
        <p>
          <b>Table</b> shows today&apos;s standings, and all-time behind the
          toggle. The column with the arrow is the one the table is sorted by.
        </p>
        <p className="muted small">
          Match points are wins, plus a bonus for winning heavily or from a
          deuce, minus a point for losing heavily. The table shows each part, so
          the arithmetic is always checkable — you should never have to take a
          number on trust.
        </p>
      </Step>

      <Step n={6} title="Finish and share">
        <p>
          At the end, tap <b>End</b> and finish the session. Until you do, the
          games count towards nothing — the standings for the day are there, but
          nobody&apos;s all-time figures move.
        </p>
        <p>
          Then <b>Share the summary</b> makes a picture of the results to send to
          the group.
        </p>
      </Step>

      {/* FR-TOUR-4. Short, and after the ordinary steps rather than among
          them: most people will never see a tournament day, and the ones who
          do will read this on the morning wanting two facts — what the
          scoring is, and why the app is telling them who to play. */}
      <section className="block">
        <h3 className="card-head">On a tournament day</h3>
        <p>
          The <b>Play</b> screen looks different: both courts are on it, and the
          four on each are the ones the draw picked. There is nobody to choose
          and no rotation to argue about — tap <b>Won</b> on your court, tap
          the score, done.
        </p>
        <p>
          The scoring is the tournament&apos;s, not the club&apos;s. A win is
          worth one point however it was won, and if the two teams finish level
          the score difference decides it. The game may also stop short of the
          usual cap — the card says what it is, and the score screen only ever
          offers results that can actually happen.
        </p>
        <p className="muted small">
          If somebody does not turn up, an admin can put someone else in their
          place from <b>Table</b>. Every game still to be played follows; the
          ones already logged keep whoever really played them.
        </p>
      </section>

      <section className="block">
        <h3 className="card-head">If anything goes wrong</h3>
        <p>
          There is a paper-equivalent tracker that needs no account and no
          signal, and it keeps working when the app does not. It is worth opening
          once now so you know where it is, rather than on the morning you need
          it.
        </p>
        <a className="escape-hatch" href="/backup/">
          <span>Open the backup tracker</span>
          <span>works without the app</span>
        </a>
      </section>

      <button className="primary wide" onClick={onClose}>
        Got it
      </button>
    </main>
  );
}

/**
 * One step, numbered.
 *
 * FR-HELP-1 asks that no step require scrolling past the one before it, which
 * is a layout property rather than a content one — hence the cards: each step
 * is a block a thumb can stop at, instead of a wall of prose where losing your
 * place means starting again.
 */
function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="block step">
      <h3 className="card-head">
        <span className="stepno">{n}</span>
        {title}
      </h3>
      {children}
    </section>
  );
}
